/* eslint-disable jsx-a11y/label-has-associated-control */
import * as React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { validatePassword, getPasswordStrengthColor, getPasswordRequirements } from 'utils/passwordValidation';
// Remove the import for client-side hashing

// eslint-disable-next-line flowtype/no-types-missing-file-annotation
function ChangePasswordForm({ username, setErrorMessage, setSuccessMessage, changePassword }: any): React$Element<any> {
  const [passwordValidation, setPasswordValidation] = React.useState(null);
  const [showRequirements, setShowRequirements] = React.useState(false);

  return (
    <Formik
      initialValues={{
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      }}
      validate={(values) => {
        const errors = {};
        const contextualStrings = [username, 'tacs', 'advik'];

        if (!values.oldPassword) {
          errors.oldPassword = 'Required';
        }

        if (!values.newPassword) {
          errors.newPassword = 'Required';
        } else {
          // Validate new password strength
          const validation = validatePassword(values.newPassword, contextualStrings);
          setPasswordValidation(validation);

          if (!validation.isValid) {
            errors.newPassword = validation.errors[0]; // Show first error
          }
        }

        if (!values.confirmPassword) {
          errors.confirmPassword = 'Required';
        } else if (values.newPassword !== values.confirmPassword) {
          errors.confirmPassword = 'Passwords do not match';
        }

        return errors;
      }}
      onSubmit={async (values, { setSubmitting }) => {
        setTimeout(() => {
          try {
            // Send plaintext passwords to the server
            // They will be protected in transit by HTTPS
            const payload = {
              oldPassword: values.oldPassword, // Send plaintext old password (protected by HTTPS)
              newPassword: values.newPassword, // Send plaintext new password (protected by HTTPS)
            };
            changePassword(payload);
            setSubmitting(false);
          } catch (error) {
            console.error('Error in password change:', error);
            setSubmitting(false);
          }
        }, 400);
      }}
    >
      {({ errors, touched, isSubmitting }) => (
        <Form className="col-md-12 p-0">
          <div className="form-group">
            <label className="form-label d-block" htmlFor="oldPassword">
              Old Password:
            </label>
            <Field
              type="password"
              name="oldPassword"
              id="oldPassword"
              aria-label="Old Password"
              className={`form-control ${errors.oldPassword && touched.oldPassword ? 'is-invalid' : touched.oldPassword && 'is-valid'}`}
              placeholder="Old Password"
            />
            <ErrorMessage name="oldPassword" render={(msg) => <div className="invalid-feedback">{msg}</div>} />
          </div>
          <div className="form-group">
            <label className="form-label d-block" htmlFor="newPassword">
              New Password:
            </label>
            <Field
              type="password"
              name="newPassword"
              id="newPassword"
              aria-label="New Password"
              className={`form-control ${errors.newPassword && touched.newPassword ? 'is-invalid' : touched.newPassword && 'is-valid'}`}
              placeholder="New Password"
              onFocus={() => setShowRequirements(true)}
              onBlur={() => setShowRequirements(false)}
            />
            <ErrorMessage name="newPassword" render={(msg) => <div className="invalid-feedback">{msg}</div>} />

            {/* Password Strength Indicator */}
            {passwordValidation && touched.newPassword && (
              <div className="mt-2">
                <div className="d-flex align-items-center">
                  <small className="text-muted mr-2">Strength:</small>
                  <span
                    style={{
                      color: getPasswordStrengthColor(passwordValidation.strength),
                      fontWeight: 'bold',
                    }}
                  >
                    {passwordValidation.strength}
                  </span>
                  <span className="ml-2 text-muted">({passwordValidation.score}/75)</span>
                </div>
                <div className="progress mt-1" style={{ height: '4px' }}>
                  <div
                    className="progress-bar"
                    style={{
                      width: `${Math.min((passwordValidation.score / 75) * 100, 100)}%`,
                      backgroundColor: getPasswordStrengthColor(passwordValidation.strength),
                    }}
                  />
                </div>
              </div>
            )}

            {/* Password Requirements */}
            {showRequirements && (
              <div className="mt-2 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '12px' }}>
                <strong>Password Requirements:</strong>
                <ul className="mb-0 mt-1 pl-3">
                  {getPasswordRequirements().map((requirement, index) => (
                    <li key={index} className="text-muted">
                      {requirement}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Password Validation Errors */}
            {passwordValidation && !passwordValidation.isValid && touched.newPassword && (
              <div className="mt-2">
                <small className="text-danger">
                  <strong>Issues to fix:</strong>
                  <ul className="mb-0 mt-1 pl-3">
                    {passwordValidation.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </small>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label d-block" htmlFor="confirmPassword">
              Confirm Password:
            </label>
            <Field
              type="password"
              name="confirmPassword"
              id="confirmPassword"
              aria-label="Confirm Password"
              className={`form-control ${errors.confirmPassword && touched.confirmPassword ? 'is-invalid' : touched.confirmPassword && 'is-valid'}`}
              placeholder="Confirm Password"
            />
            <ErrorMessage name="confirmPassword" render={(msg) => <div className="invalid-feedback">{msg}</div>} />
          </div>

          {setErrorMessage && (
            <div className="alert alert-soft-danger">
              <div className="d-flex flex-wrap">
                <div className="mr-8pt">
                  <i className="material-icons">report</i>
                </div>
                <div className="flex" style={{ minWidth: '180px' }}>
                  <small className="text-100">{setErrorMessage}</small>
                </div>
              </div>
            </div>
          )}

          {setSuccessMessage && (
            <div className="alert alert-soft-success">
              <div className="d-flex flex-wrap">
                <div className="mr-8pt">
                  <i className="material-icons">check_circle</i>
                </div>
                <div className="flex" style={{ minWidth: '180px' }}>
                  <small className="text-100">{setSuccessMessage}</small>
                </div>
              </div>
            </div>
          )}

          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Updating Password...' : 'Update Password'}
          </button>
        </Form>
      )}
    </Formik>
  );
}

export default ChangePasswordForm;
